import { all, call, put, takeEvery, takeLeading } from 'redux-saga/effects';
import { PromiseType } from 'utility-types';

import GLOBAL_ERROR_MESSAGE from '@app/constants/errorMessages';
import workspaceService from '@app/libs/workspaceService';
import {
  DOCUMENT_ATTACHMENTS__DOWNLOAD_FAILURE,
  DOCUMENT_ATTACHMENTS__DOWNLOAD_REQUEST,
  DOCUMENT_ATTACHMENTS__DOWNLOAD_SUCCESS,
  DOCUMENT_ATTACHMENTS__FETCH_REQUEST,
  DOCUMENT_ATTACHMENTS__OPEN_FAILURE,
  DOCUMENT_ATTACHMENTS__OPEN_REQUEST,
  DOCUMENT_ATTACHMENTS__OPEN_SUCCESS,
} from '@app/store/actionTypes';
import {
  downloadAttachment,
  downloadAttachmentFailure,
  downloadAttachmentSuccess,
  fetchAttachments,
  fetchAttachmentsFailure,
  fetchAttachmentsSuccess,
  openAttachment,
  openAttachmentFailure,
  openAttachmentSuccess,
} from '@app/store/document/attachments/actions';
import File from '@app/store/document/attachments/File';
import { modalOpen } from '@app/store/modal/actions';
import { getDocumentsAttachmentsDeprecated } from '@app/store/transport-layer/document/documents/attachments';
import { getDocumentsAttachmentsFile } from '@app/store/transport-layer/document/documents/attachments/file';
import { downloadFileFromURLSaga, openFileFromURLSaga } from '@app/store/transport-layer/files';

export function* downloadAttachmentSaga({
  payload: { attachmentId, documentId, downloadFileName },
}: ReturnType<typeof downloadAttachment>) {
  try {
    const workspaceId = workspaceService.getWorkspaceId();
    const { file }: { file: File } = yield call(getDocumentsAttachmentsFile, {
      attachmentId,
      documentId,
      workspaceId,
    });
    yield put(
      downloadAttachmentSuccess({
        downloadFileName,
        file,
      })
    );
  } catch (error) {
    yield put(downloadAttachmentFailure(error));
  }
}

export function* downloadAttachmentSuccessSaga({
  payload: { file, downloadFileName },
}: ReturnType<typeof downloadAttachmentSuccess>) {
  const { url, extension } = file;
  try {
    yield call(downloadFileFromURLSaga, {
      url,
      saveFileName: `${downloadFileName}${extension}`,
    });
  } catch (error) {
    yield put(
      modalOpen('CONFIRM', {
        title: '다운로드 실패',
        content: GLOBAL_ERROR_MESSAGE[error.message] || error.message,
      })
    );
  }
}

export function* downloadAttachmentFailureSaga({
  payload: { message },
}: ReturnType<typeof downloadAttachmentFailure>) {
  yield put(
    modalOpen('CONFIRM', {
      title: '다운로드 실패',
      content: GLOBAL_ERROR_MESSAGE[message] || message,
    })
  );
}

export function* fetchAttachmentsSaga({
  payload: { documentId },
}: ReturnType<typeof fetchAttachments>) {
  try {
    const workspaceId = workspaceService.getWorkspaceId();
    const response: PromiseType<ReturnType<typeof getDocumentsAttachmentsDeprecated>> = yield call(
      getDocumentsAttachmentsDeprecated,
      {
        documentId,
        workspaceId,
      }
    );
    yield put(fetchAttachmentsSuccess(response));
  } catch (error) {
    yield put(fetchAttachmentsFailure(error));
  }
}

export function* openAttachmentSuccessSaga({
  payload: { file },
}: ReturnType<typeof openAttachmentSuccess>) {
  yield call(openFileFromURLSaga, file.url);
}

export function* openAttachmentSaga({
  payload: { attachmentId, documentId },
}: ReturnType<typeof openAttachment>) {
  try {
    const workspaceId = workspaceService.getWorkspaceId();
    const { file }: { file: File } = yield call(getDocumentsAttachmentsFile, {
      workspaceId,
      attachmentId,
      documentId,
    });
    yield put(
      openAttachmentSuccess({
        file: { url: file.url },
      })
    );
  } catch (error) {
    yield put(openAttachmentFailure({ documentId, error }));
  }
}

export function* openAttachmentFailureSaga({ payload }: ReturnType<typeof openAttachmentFailure>) {
  const { documentId, error } = payload;
  const { message } = error;
  yield put(
    modalOpen('CONFIRM', {
      title: '첨부파일 확인 실패',
      content: GLOBAL_ERROR_MESSAGE[message] || message,
    })
  );

  // 첨부파일정보 다시 조회하기
  yield put(fetchAttachments({ documentId }));
}

export default function* saga() {
  yield all([
    takeLeading(DOCUMENT_ATTACHMENTS__DOWNLOAD_REQUEST, downloadAttachmentSaga),
    takeEvery([DOCUMENT_ATTACHMENTS__DOWNLOAD_SUCCESS], downloadAttachmentSuccessSaga),
    takeEvery([DOCUMENT_ATTACHMENTS__DOWNLOAD_FAILURE], downloadAttachmentFailureSaga),
    takeLeading(DOCUMENT_ATTACHMENTS__FETCH_REQUEST, fetchAttachmentsSaga),
    takeEvery(DOCUMENT_ATTACHMENTS__OPEN_REQUEST, openAttachmentSaga),
    takeEvery([DOCUMENT_ATTACHMENTS__OPEN_SUCCESS], openAttachmentSuccessSaga),
    takeEvery([DOCUMENT_ATTACHMENTS__OPEN_FAILURE], openAttachmentFailureSaga),
  ]);
}
